import { useState } from 'react';
import type { Partner } from '@pomoshnik/shared';
import {
  buildCallLink,
  buildMaxOpenLink,
  buildTelegramPhoneLink,
  buildWhatsAppLink,
} from '@/lib/messengerLinks';
import { copyShareTextToClipboard } from '@/lib/clipboardCopy';
import { openExternal } from '@/lib/openExternal';
import { CITY_TO_LANGUAGE, shopMessage } from '../shops/shopMessage';

/**
 * Кнопки «Написать» подрядчику на карточке партнёра 2.8.3 (Блок 8, Фаза D3).
 *
 * Способ согласован в STOP #1 (вариант «В», approve Aibek 2026-06-15) и с потоком
 * «Ручки» (COORD 2026-06-14): переиспользуем ПУБЛИЧНЫЕ билдеры `@/lib/messengerLinks`
 * + `clipboardCopy`/`openExternal` + `shopMessage(kind='partner')`, БЕЗ правки общего
 * презентера `features/shops/ShopsListView` (Ручки-зона, «не меняем»). Единственный
 * общий файл, который трогает D3, — `shopMessage.ts` (добавлен kind 'partner').
 *
 * P-031 анти-парсинг (инварианты подтверждены Ручками, ADR-0064/Aibek 2026-06-10):
 *  - ВСЕ кнопки — `<button>`, НИКАКИХ `<a href>`.
 *  - Номера НЕТ в статичном DOM: ни в тексте кнопки, ни в `aria-label`. Номер живёт
 *    только в JS-замыканиях (`onActivate`/`getOpenUrl`) и появляется в DOM лишь в
 *    оверлее MAX/Telegram ПРИ КЛИКЕ. Дубли канала — ИНДЕКСОМ «(2)», не номером.
 *  - Ссылка строится ПРИ КЛИКЕ из замыкания.
 *  - WhatsApp и «Позвонить» (`tel:`) — `open` (открываются сразу).
 *  - Telegram И MAX — двухшаговый flow (`action`): тап → копируем СООБЩЕНИЕ в буфер →
 *    центр-оверлей с инструкцией + явная кнопка открытия (мессенджер открывается
 *    только по ней). Чата-по-номеру нет ни в TG, ни в MAX → префилла нет.
 *  - Группировка по каналу: WhatsApp → Telegram → MAX → «Позвонить». Вертикально (P-019).
 *  - username-каналы (`partner.telegram`/`partner.maxId`) НЕ рендерятся — экран только
 *    для прямой связи по номеру (как ShopsListView). Модель кнопок гонится от
 *    `partner.whatsapp` (мобильный-сигнал), «Позвонить» — по уникальным номерам.
 *  - Сообщение без ПДн пользователя (P-009): «Здравствуйте 👋», < 2000 символов.
 *
 * NB(дублирование): двухшаговый оверлей/модель кнопок здесь — упрощённая (одиночный
 * контакт) копия приватной механики `ShopsListView`. Согласовано в STOP #1 как
 * временное: при платформенном переходе на СЕРВЕРНЫЙ редирект (Ручки ведут ADR,
 * Партнёры — 5-й потребитель) все потребители мигрируют разом и дубль схлопнётся.
 * Зафиксировано в BACKLOG (B-252).
 */
export interface PartnerContactButtonsProps {
  partner: Partner;
}

/** Контекст оверлея-инструкции двухшагового flow (TG/MAX). Зеркало ShopsListView. */
interface CopyPrompt {
  copyText: string;
  instruction: string;
  openLabel: string;
  /** Строит цель ПРИ КЛИКЕ (анти-парсинг: номер не в статичной строке DOM). */
  getOpenUrl: () => string;
}

type ChannelButton =
  | { variant: 'open'; id: string; text: string; aria: string; onActivate: () => void }
  | { variant: 'action'; id: string; text: string; aria: string; prompt: CopyPrompt };

const digitsOf = (s: string): string => s.replace(/\D/g, '');

/** Подпись канала с индекс-дизамбигуацией (1-й — без суффикса, далее «(N)»). */
const channelText = (base: string, index: number): string =>
  index === 0 ? base : `${base} (${index + 1})`;
/** aria канала: подпись + «подрядчику <name>» (БЕЗ номера, P-031). */
const channelAria = (base: string, index: number, name: string): string =>
  `${channelText(base, index)} подрядчику ${name}`;

/**
 * Кнопки прямой связи с подрядчиком, сгруппированные ПО КАНАЛУ: WhatsApp → Telegram →
 * MAX → «Позвонить». Модель (как ShopsListView, упрощённая под одиночный контакт):
 *  - `partner.whatsapp` (валидный мобильный) → WhatsApp (open) + Telegram (action) +
 *    MAX (action);
 *  - «Позвонить» (open, tel:) — для КАЖДОГО уникального номера
 *    (`whatsapp` ∪ `phoneMobile` ∪ `phoneLandline`, дедуп по цифрам).
 * Невалидный мобильный пропускается в WA/TG/MAX, но «Позвонить» строится независимо.
 */
function buildPartnerButtons(partner: Partner, message: string): ChannelButton[] {
  const name = partner.name;
  const waButtons: ChannelButton[] = [];
  const tgButtons: ChannelButton[] = [];
  const maxButtons: ChannelButton[] = [];

  // partner.whatsapp — одиночный мобильный-сигнал (как whatsapp[] в ShopsListView).
  const whatsapp = partner.whatsapp;
  if (whatsapp) {
    try {
      // Валидация: строим ссылки и отбрасываем (реальная сборка — ПРИ КЛИКЕ, P-031).
      buildWhatsAppLink(whatsapp, message);
      buildTelegramPhoneLink(whatsapp);
      const index = 0; // одиночный контакт → без суффикса
      waButtons.push({
        variant: 'open',
        id: 'wa',
        text: channelText('Написать в WhatsApp', index),
        aria: channelAria('Написать в WhatsApp', index, name),
        onActivate: () => openExternal(buildWhatsAppLink(whatsapp, message)),
      });
      tgButtons.push({
        variant: 'action',
        id: 'tg',
        text: channelText('Написать в Telegram', index),
        aria: channelAria('Написать в Telegram', index, name),
        prompt: {
          copyText: message,
          instruction:
            'Сообщение скопировано. Откройте чат и вставьте: зажмите поле ввода → Вставить',
          openLabel: 'Открыть Telegram',
          getOpenUrl: () => buildTelegramPhoneLink(whatsapp),
        },
      });
      maxButtons.push({
        variant: 'action',
        id: 'max',
        text: channelText('Написать в MAX', index),
        aria: channelAria('Написать в MAX', index, name),
        prompt: {
          // MAX: чата-по-номеру нет; копируем САМО СООБЩЕНИЕ (как Telegram). Номер
          // показываем ТОЛЬКО в оверлее (рендерится при клике) — в статичном DOM
          // номера нет (P-031).
          copyText: message,
          instruction: `Найдите подрядчика в MAX по номеру ${whatsapp}, сообщение в буфере — вставьте`,
          openLabel: 'Открыть MAX',
          getOpenUrl: () => buildMaxOpenLink(),
        },
      });
    } catch {
      // невалидный мобильный — WA/TG/MAX пропускаем; «Позвонить» ниже независимо.
    }
  }

  // «Позвонить» — для каждого уникального номера (whatsapp ∪ phoneMobile ∪ phoneLandline,
  // дедуп по цифрам). Порядок: whatsapp → phoneMobile → phoneLandline.
  const callButtons: ChannelButton[] = [];
  const seenCallDigits = new Set<string>();
  [partner.whatsapp, partner.phoneMobile, partner.phoneLandline].forEach((phone) => {
    if (!phone) return;
    const d = digitsOf(phone);
    if (seenCallDigits.has(d)) return;
    seenCallDigits.add(d);
    try {
      buildCallLink(phone); // валидация; реальная сборка — при клике (P-031)
    } catch {
      return; // невалидный телефон — пропускаем
    }
    const index = callButtons.length;
    callButtons.push({
      variant: 'open',
      id: `call-${index}`,
      text: channelText('Позвонить', index),
      aria: channelAria('Позвонить', index, name),
      onActivate: () => openExternal(buildCallLink(phone)),
    });
  });

  return [...waButtons, ...tgButtons, ...maxButtons, ...callButtons];
}

const BUTTON_CLASS = [
  'block w-full min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink',
  'font-semibold text-base text-center py-3 px-2',
  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
].join(' ');

export function PartnerContactButtons({ partner }: PartnerContactButtonsProps) {
  // Оверлей-инструкция двухшагового flow (TG/MAX). Персистентный центр-оверлей —
  // как ShopsListView: висит пока пользователь не закроет (крестик/тап вне).
  const [copyPrompt, setCopyPrompt] = useState<CopyPrompt | null>(null);
  const dismissPrompt = () => setCopyPrompt(null);

  const language = CITY_TO_LANGUAGE[partner.city] ?? 'ru';
  // partner-шаблон самодостаточен («Здравствуйте 👋») — товар/бренд/URL игнорируются.
  const message = shopMessage({ kind: 'partner', productName: '', globalId: '', language });

  const buttons = buildPartnerButtons(partner, message);

  // Шаг 1: копируем СИНХРОННО внутри клика и показываем оверлей. Мессенджер НЕ
  // открываем здесь — открытие во второй шаг, по явной кнопке оверлея.
  const handleAction = (prompt: CopyPrompt) => {
    void copyShareTextToClipboard(prompt.copyText);
    setCopyPrompt(prompt);
  };

  // Шаг 2: явная кнопка открытия. Ссылку строим ПРИ КЛИКЕ из замыкания (P-031).
  const handleOpenFromPrompt = () => {
    if (copyPrompt) openExternal(copyPrompt.getOpenUrl());
  };

  const renderButton = (b: ChannelButton) => (
    <button
      key={b.id}
      type="button"
      onClick={b.variant === 'open' ? b.onActivate : () => handleAction(b.prompt)}
      aria-label={b.aria}
      className={BUTTON_CLASS}
    >
      {b.text}
    </button>
  );

  return (
    <>
      {/* Вертикальный список кнопок прямой связи (P-019), сгруппирован по каналу. */}
      <div className="flex flex-col gap-3">
        {buttons.map(renderButton)}
        {buttons.length === 0 && (
          <p className="text-muted text-sm">У подрядчика пока нет контактов.</p>
        )}
      </div>

      {copyPrompt && (
        // Двухшаговый flow: центр-оверлей с инструкцией + явная кнопка открытия.
        // Закрытие — крестиком или тапом по затемнённому фону (backdrop).
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
          <button
            type="button"
            aria-label="Закрыть подсказку"
            onClick={dismissPrompt}
            className="absolute inset-0 bg-ink/40 cursor-default"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Как отправить сообщение"
            className="relative z-10 w-full max-w-[320px] rounded-xl bg-card text-ink shadow-card p-5 pt-10 flex flex-col gap-4"
          >
            <button
              type="button"
              aria-label="Закрыть"
              onClick={dismissPrompt}
              className="absolute top-2 right-2 min-h-tap min-w-[44px] text-ink text-2xl leading-none"
            >
              <span aria-hidden="true">✕</span>
            </button>
            <p role="status" aria-live="polite" className="text-ink text-base m-0">
              {copyPrompt.instruction}
            </p>
            <button type="button" onClick={handleOpenFromPrompt} className={BUTTON_CLASS}>
              {copyPrompt.openLabel}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
